import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import ModuleAssignment from './ModuleAssignment';
import CreateSchoolAdmin from './CreateSchoolAdmin';
import axios from 'axios';

interface School {
  id: string;
  name: string;
  code: string;
}

/**
 * Module Assignment Page (SuperAdmin)
 * Allows selecting a school and managing its modules and admins
 */
const ModuleAssignmentPage: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      const response = await axios.get('/schools');
      setSchools(response.data);
      if (response.data.length > 0) {
        // Optional: auto-select first school
        // setSelectedSchoolId(response.data[0].id);
      }
    } catch (err: any) {
      setError('Failed to fetch schools');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolChange = (event: any) => {
    setSelectedSchoolId(event.target.value);
  };

  const handleModuleUpdate = () => {
    // Callback when modules are updated
    console.log('Modules updated');
  };

  if (loading) {
    return (
      <MainLayout>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            School Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Select a school to manage its modules and administrators.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel id="school-select-label">Select School</InputLabel>
            <Select
              labelId="school-select-label"
              value={selectedSchoolId}
              label="Select School"
              onChange={handleSchoolChange}
            >
              {schools.map((school) => (
                <MenuItem key={school.id} value={school.id}>
                  {school.name} ({school.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {selectedSchoolId && (
          <Paper sx={{ width: '100%' }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Module Management" icon={<SettingsIcon />} iconPosition="start" />
              <Tab label="Create Admin" icon={<PersonAddIcon />} iconPosition="start" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {tabValue === 0 && (
                <ModuleAssignment
                  schoolId={selectedSchoolId}
                  onUpdate={handleModuleUpdate}
                />
              )}
              {tabValue === 1 && (
                <CreateSchoolAdmin
                  schoolId={selectedSchoolId}
                  onSuccess={() => {
                    // Optional: maybe show a success message or refresh something
                  }}
                />
              )}
            </Box>
          </Paper>
        )}
      </Container>
    </MainLayout>
  );
};

export default ModuleAssignmentPage;
